import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';

import { CracMapService } from '../services/cracmap.service';
import {
    OrganizationState,
    OrganizationStateModel,
} from '../../../admin/organization/organization.state';
import { CracMapData } from '../cracmap.component';
import { LineString } from 'geojson';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface CracMapModel {
    data: {
        [organization_id: string]: {
            [year: number]: {
                data: CracMapData[];
            };
        };
    };
    metropolitanBorder: LineString;
}

//Constants

//Action loaded on page init : ask store to retreive all data from a list of keys
export class PrepareData {
    static readonly type = 'CRACMAP/PREPARE_DATA_FOR_PAGE';
    constructor() {}
}

export class LoadMetropolitanBorder {
    static readonly type = 'CRACMAP/LOAD_METROPOLITAN_BORDER';
    constructor() {}
}

//Actions
const NAME = 'cracMap';
@State<CracMapModel>({
    name: NAME,
    defaults: {
        data: {},
        metropolitanBorder: { type: 'LineString', coordinates: [] },
    },
})
@Injectable()
export class CracMapState {
    constructor(
        private readonly cracMapService: CracMapService,
        private readonly store: Store,
    ) {}

    public static getDatas() {
        return createSelector(
            [OrganizationState, CracMapState],
            (organizationState: OrganizationStateModel, state: CracMapModel): CracMapData[] => {
                const currentOrganization = organizationState.currentOrganization;
                const currentYear = organizationState.currentYear;
                try {
                    return state.data[currentOrganization?.id!][currentYear].data || [];
                } catch {
                    return [{ year: currentYear, error: true }];
                }
            },
        );
    }

    @Selector()
    public static metropolitanBorder(state: CracMapModel): LineString {
        return state.metropolitanBorder;
    }

    @Action(LoadMetropolitanBorder)
    public async loadMetropolitanBorder(
        ctx: StateContext<CracMapModel>,
        action: LoadMetropolitanBorder,
    ): Promise<void> {
        const state = ctx.getState();
        if (state.metropolitanBorder && state.metropolitanBorder.coordinates.length > 0) return;

        const data = await this.cracMapService.getMetropolitanBorder();
        if (data) {
            ctx.patchState({ metropolitanBorder: data });
        }
    }

    @Action(PrepareData)
    public async prepareData(ctx: StateContext<CracMapModel>, action: PrepareData): Promise<void> {
        const currentOrganization = this.store.selectSnapshot(
            OrganizationState.currentOrganization,
        );
        const currentYear = this.store.selectSnapshot(OrganizationState.currentYear);

        if (!currentOrganization || !currentYear) {
            return;
        }

        const state = ctx.getState();
        state.data[currentOrganization.id] = state.data[currentOrganization.id] || {};
        state.data[currentOrganization.id][currentYear] = state.data[currentOrganization.id][
            currentYear
        ] || { data: [] };

        try {
            //Ignore if data already exist
            if (
                state.data[currentOrganization.id][currentYear].data[0]?.id &&
                !state.data[currentOrganization.id][currentYear].data[0]?.error
            ) {
                return;
            }
            const data = await this.cracMapService.get(currentOrganization.id, currentYear);
            if (data?.length > 0) {
                state.data[currentOrganization.id][currentYear].data = data;
            } else {
                state.data[currentOrganization.id][currentYear].data = [
                    {
                        year: currentYear,
                        error: true,
                    },
                ];
            }
        } catch (error: any) {
            state.data[currentOrganization.id][currentYear].data = [
                {
                    year: currentYear,
                    error: true,
                },
            ];
        } finally {
            ctx.patchState(state);
            this.store.dispatch(new LoadMetropolitanBorder());
            return;
        }
    }
}
